import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatPhoneInput } from '@/lib/utils';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, ArrowLeft, ArrowRight, TrendingUp, Users, GraduationCap,
  Headset, Rocket, ShieldCheck, Sparkles, Star, Check, Globe,
} from 'lucide-react';
import { publicAPI } from '@/lib/publicApi';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR',
];

const empty = {
  first_name: '', last_name: '', email: '', phone: '', state: '',
  is_licensed: null, npn: '', current_agency: '', years_experience: '',
  areas_of_interest: [], comments: '', consent_accepted: false, website: '',
};

const T = {
  es: {
    badge: 'Programa de Socios',
    heroTitle: 'Construye tu práctica con KR Enterprise Group.',
    heroSub: 'Únete a un equipo que invierte en ti — compensación premium, leads calientes, entrenamiento de élite y un equipo de apoyo que te respalda. Toma menos de dos minutos.',
    benefits: [
      { icon: TrendingUp, title: 'Comisiones de primer nivel', text: 'Pagos líderes en la industria, bonos y un plan de compensación transparente.' },
      { icon: Users, title: 'Leads calificados', text: 'Un flujo constante de leads tibios y pre-filtrados para ti.' },
      { icon: GraduationCap, title: 'Entrenamiento de élite', text: 'Mentoría uno a uno y un plan probado para crecer rápido.' },
      { icon: Headset, title: 'Soporte completo', text: 'Un back-office dedicado para que te enfoques en vender.' },
      { icon: Rocket, title: 'Onboarding VIP', text: 'Un concierge te contrata y te pone a producir rápido.' },
      { icon: ShieldCheck, title: 'Tecnología de punta', text: 'Una plataforma moderna para CRM, cotizaciones y clientes.' },
    ],
    quote: '"El onboarding fue impecable y el soporte no tiene comparación. Empecé a producir más rápido de lo que imaginé."',
    quoteBy: '— Un agente socio de KR',
    steps: ['Tú', 'Licencia', 'Interés', 'Finalizar'],
    s1Title: 'Cuéntanos de ti', s1Sub: 'Lo básico para que nuestro equipo te contacte.',
    first: 'Nombre', last: 'Apellido', email: 'Correo', phone: 'Celular', state: 'Estado', select: 'Selecciona…',
    s2Title: 'Licencia y experiencia', s2Sub: 'Esto nos ayuda a personalizar tu onboarding.',
    licensedQ: '¿Tienes licencia actualmente?', yes: 'Sí', no: 'No',
    npn: 'NPN (opcional)', agency: 'Agencia o FMO actual (opcional)', years: 'Años de experiencia (opcional)',
    s3Title: '¿Qué productos te interesan más?', s3Sub: 'Elige los que apliquen — opcional.', s3Skip: 'Puedes saltar este paso.',
    s4Title: 'Revisar y enviar', s4Sub: 'Un último vistazo antes de conectar.',
    comments: '¿Algo más que quieras que sepamos? (opcional)',
    reviewName: 'Nombre', reviewEmail: 'Correo', reviewPhone: 'Celular', reviewState: 'Estado', reviewLicensed: 'Con licencia', reviewInterests: 'Intereses',
    consentFallback: 'Doy mi consentimiento para que KR Enterprise Group me contacte por correo y teléfono sobre oportunidades de sociedad como agente.',
    cancel: 'Cancelar', back: 'Atrás', continue: 'Continuar', submit: 'Enviar solicitud', submitting: 'Enviando…',
    errReq: 'Requerido', errEmail: 'Correo válido requerido', errPhone: 'Celular válido requerido', errLic: 'Elige Sí o No', errConsent: 'El consentimiento es requerido',
    genericErr: 'Algo salió mal. Inténtalo de nuevo.',
    successBadge: 'Solicitud recibida', successTitle: '¡Bienvenido al siguiente paso!',
    successBody: 'Gracias por aplicar para ser socio de KR Enterprise Group. Un miembro de nuestro equipo de reclutamiento te contactará en breve para explicarte lo que sigue.',
    home: 'Volver al inicio',
  },
  en: {
    badge: 'Partner Program',
    heroTitle: 'Build your practice with KR Enterprise Group.',
    heroSub: 'Join a team that invests in you — premium compensation, warm leads, elite training and a support crew that has your back. Takes under two minutes.',
    benefits: [
      { icon: TrendingUp, title: 'Top-tier commissions', text: 'Industry-leading payouts, bonuses and a transparent comp plan.' },
      { icon: Users, title: 'Qualified leads', text: 'A steady flow of warm, pre-screened leads handed to you.' },
      { icon: GraduationCap, title: 'Elite training', text: 'One-on-one mentorship and a proven playbook to scale fast.' },
      { icon: Headset, title: 'Full back-office', text: 'Dedicated support so you can focus on selling, not paperwork.' },
      { icon: Rocket, title: 'White-glove onboarding', text: 'A concierge gets you contracted and producing quickly.' },
      { icon: ShieldCheck, title: 'Best-in-class tech', text: 'A modern platform for CRM, quoting and client management.' },
    ],
    quote: '"The onboarding was seamless and the support is unmatched. I was writing business faster than I ever imagined."',
    quoteBy: '— A KR partner agent',
    steps: ['You', 'Licensing', 'Interests', 'Review'],
    s1Title: "Let's get to know you", s1Sub: 'The basics so our team can reach out.',
    first: 'First name', last: 'Last name', email: 'Email', phone: 'Mobile phone', state: 'State', select: 'Select…',
    s2Title: 'Licensing & experience', s2Sub: 'This helps us tailor your onboarding.',
    licensedQ: 'Are you currently licensed?', yes: 'Yes', no: 'No',
    npn: 'NPN (optional)', agency: 'Current agency or FMO (optional)', years: 'Years of experience (optional)',
    s3Title: 'What products interest you most?', s3Sub: 'Pick any that apply — optional.', s3Skip: 'You can skip this step.',
    s4Title: 'Review & submit', s4Sub: 'One last look before we connect.',
    comments: "Anything else you'd like us to know? (optional)",
    reviewName: 'Name', reviewEmail: 'Email', reviewPhone: 'Phone', reviewState: 'State', reviewLicensed: 'Licensed', reviewInterests: 'Interests',
    consentFallback: 'I consent to being contacted by KR Enterprise Group by email and phone regarding agent-partnership opportunities.',
    cancel: 'Cancel', back: 'Back', continue: 'Continue', submit: 'Submit application', submitting: 'Submitting…',
    errReq: 'Required', errEmail: 'Valid email required', errPhone: 'Valid mobile phone required', errLic: 'Please choose Yes or No', errConsent: 'Consent is required',
    genericErr: 'Something went wrong. Please try again.',
    successBadge: 'Application received', successTitle: 'Welcome to the next step.',
    successBody: "Thank you for applying to partner with KR Enterprise Group. A member of our recruiting team will personally reach out shortly to walk you through what's next.",
    home: 'Back to home',
  },
};

const LAST_STEP = 3;

export default function BecomeAgentPage() {
  const [lang, setLang] = useState('es');
  const [form, setForm] = useState(empty);
  const [meta, setMeta] = useState({ areas_of_interest: [], consent_language: '' });
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const topRef = useRef(null);
  const t = T[lang];

  useEffect(() => {
    publicAPI.prospectMetadata().then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const toggleArea = (a) => setForm((f) => ({
    ...f,
    areas_of_interest: f.areas_of_interest.includes(a)
      ? f.areas_of_interest.filter((x) => x !== a)
      : [...f.areas_of_interest, a],
  }));

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!form.first_name.trim()) e.first_name = t.errReq;
      if (!form.last_name.trim()) e.last_name = t.errReq;
      if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = t.errEmail;
      if (form.phone.replace(/\D/g, '').length < 7) e.phone = t.errPhone;
      if (!form.state) e.state = t.errReq;
    }
    if (s === 1) {
      if (form.is_licensed === null) e.is_licensed = t.errLic;
    }
    if (s === LAST_STEP) {
      if (!form.consent_accepted) e.consent_accepted = t.errConsent;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const keepInView = () => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  const next = () => { if (!validateStep(step)) return; setStep((s) => Math.min(s + 1, LAST_STEP)); keepInView(); };
  const back = () => { setStep((s) => Math.max(s - 1, 0)); keepInView(); };

  const submit = async () => {
    if (!validateStep(LAST_STEP)) return;
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        state: form.state,
        is_licensed: !!form.is_licensed,
        areas_of_interest: form.areas_of_interest,
        preferred_language: lang === 'es' ? 'Spanish' : 'English',
        consent_accepted: true,
        website: form.website,
      };
      if (form.npn.trim()) payload.npn = form.npn.trim();
      if (form.current_agency.trim()) payload.current_agency = form.current_agency.trim();
      if (form.years_experience !== '') payload.years_experience = Number(form.years_experience);
      if (form.comments.trim()) payload.comments = form.comments.trim();

      await publicAPI.submitProspect(payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err?.response?.data?.detail || t.genericErr;
      setErrors({ _submit: typeof detail === 'string' ? detail : t.genericErr });
    } finally {
      setSubmitting(false);
    }
  };

  const progress = useMemo(() => Math.round(((step + 1) / (LAST_STEP + 1)) * 100), [step]);

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a1f47] text-white flex items-center" data-testid="become-agent-success">
        <div className="max-w-xl mx-auto px-6 py-24 text-center">
          <div className="w-16 h-16 rounded-full bg-[#f5c876] text-[#0a1f47] flex items-center justify-center mx-auto shadow-lg shadow-amber-900/30">
            <CheckCircle2 size={32} />
          </div>
          <span className="mt-6 inline-block text-xs font-semibold uppercase tracking-[0.2em] text-[#f5c876]">{t.successBadge}</span>
          <h1 className="mt-3 text-3xl font-bold">{t.successTitle}</h1>
          <p className="mt-4 text-slate-300 leading-relaxed">{t.successBody}</p>
          <Link to="/" className="mt-8 inline-flex items-center gap-2 text-[#f5c876] font-semibold hover:underline">
            <ArrowLeft size={16} /> {t.home}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="become-agent-page">
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,560px)] lg:min-h-screen">
        {/* ── VIP brand / benefits panel ── */}
        <aside className="relative overflow-hidden bg-[#0a1f47] text-white px-6 sm:px-10 lg:px-14 py-12 lg:py-16">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#1a3a7a]/60 blur-3xl" aria-hidden />
          <div className="absolute bottom-0 -left-20 w-72 h-72 rounded-full bg-[#b8862d]/20 blur-3xl" aria-hidden />
          <div className="relative max-w-xl mx-auto lg:mx-0 lg:ml-auto lg:max-w-md">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#f5c876]/40 bg-[#f5c876]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#f5c876]">
              <Sparkles size={13} /> {t.badge}
            </span>
            <h1 className="mt-5 text-3xl sm:text-4xl font-bold leading-tight tracking-tight">{t.heroTitle}</h1>
            <p className="mt-4 text-slate-300 leading-relaxed">{t.heroSub}</p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {t.benefits.map((b) => (
                <div key={b.title} className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-3.5 transition hover:bg-white/[0.07]">
                  <b.icon size={18} className="text-[#f5c876]" />
                  <div className="mt-2 text-sm font-semibold">{b.title}</div>
                  <div className="mt-0.5 text-xs text-slate-400 leading-snug">{b.text}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.04] p-4">
              <div className="flex gap-0.5 text-[#f5c876]">
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={14} fill="currentColor" />)}
              </div>
              <p className="mt-2 text-sm text-slate-200 italic leading-relaxed">{t.quote}</p>
              <p className="mt-1 text-xs text-slate-400">{t.quoteBy}</p>
            </div>

            <div className="mt-6 rounded-xl border border-[#f5c876]/30 bg-[#f5c876]/[0.07] p-4" data-testid="ba-onboarding-cta">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <ShieldCheck size={16} className="text-[#f5c876]" /> Already a licensed agent?
              </div>
              <p className="mt-1 text-xs text-slate-300 leading-snug">
                Skip the interest form and submit your full onboarding application to get contracted.
              </p>
              <Link
                to="/apply"
                data-testid="ba-start-onboarding"
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#b8862d] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a67520] hover:-translate-y-0.5 transition-all"
              >
                Start Onboarding <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </aside>

        {/* ── Application wizard ── */}
        <main className="px-5 sm:px-8 lg:px-12 py-10 lg:py-16">
          <div ref={topRef} className="max-w-md mx-auto scroll-mt-24">
            {/* Onboarding shortcut — already-licensed agents go straight to the full application */}
            <Link
              to="/apply"
              data-testid="ba-onboarding-banner"
              className="group mb-5 flex items-center gap-3 rounded-xl border border-[#b8862d]/40 bg-gradient-to-r from-[#fff8ec] to-white px-4 py-3 transition hover:border-[#b8862d] hover:shadow-sm"
            >
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#b8862d]/15 text-[#b8862d]">
                <Rocket size={18} />
              </span>
              <span className="flex-1 text-sm leading-snug text-slate-700">
                Ready to start your onboarding?{' '}
                <span className="font-semibold text-[#b8862d] underline-offset-2 group-hover:underline">Click here</span> to begin your full agent application.
              </span>
              <ArrowRight size={16} className="flex-shrink-0 text-[#b8862d] transition group-hover:translate-x-0.5" />
            </Link>
            {/* Language toggle */}
            <div className="flex justify-end mb-5">
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-0.5 text-xs font-semibold" data-testid="ba-lang-toggle">
                <Globe size={13} className="ml-2 mr-1 text-slate-400" />
                {[['es', 'ES'], ['en', 'EN']].map(([code, label]) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setLang(code)}
                    data-testid={`ba-lang-${code}`}
                    className={`px-3 py-1 rounded-full transition ${lang === code ? 'bg-[#0f2a5e] text-white' : 'text-slate-500 hover:text-slate-800'}`}
                  >{label}</button>
                ))}
              </div>
            </div>

            {/* Stepper */}
            <div className="mb-8" data-testid="ba-stepper">
              <div className="flex items-center justify-between">
                {t.steps.map((label, i) => {
                  const done = i < step;
                  const active = i === step;
                  return (
                    <React.Fragment key={label}>
                      <div className="flex flex-col items-center gap-1.5" data-testid={`ba-step-dot-${i}`}>
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition
                          ${active ? 'bg-[#0f2a5e] text-white ring-4 ring-[#0f2a5e]/15'
                            : done ? 'bg-[#b8862d] text-white' : 'bg-slate-200 text-slate-500'}`}>
                          {done ? <Check size={16} /> : i + 1}
                        </div>
                        <span className={`text-[11px] font-medium ${active ? 'text-[#0f2a5e]' : 'text-slate-400'}`}>{label}</span>
                      </div>
                      {i < t.steps.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-1 -mt-5 rounded ${i < step ? 'bg-[#b8862d]' : 'bg-slate-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="mt-4 h-1 w-full rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#0f2a5e] to-[#b8862d] transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>

            <form onSubmit={(ev) => ev.preventDefault()} noValidate data-testid="become-agent-form">
              {errors._submit && (
                <div className="mb-5 rounded-md border border-rose-200 bg-rose-50 text-rose-800 p-3 text-sm" role="alert">{errors._submit}</div>
              )}

              {/* Step 0 — Contact */}
              {step === 0 && (
                <StepShell title={t.s1Title} subtitle={t.s1Sub}>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={t.first} required error={errors.first_name}>
                      <input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} data-testid="ba-first-name" className={inputCls} />
                    </Field>
                    <Field label={t.last} required error={errors.last_name}>
                      <input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} data-testid="ba-last-name" className={inputCls} />
                    </Field>
                    <Field label={t.email} required error={errors.email}>
                      <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="ba-email" className={inputCls} autoComplete="email" />
                    </Field>
                    <Field label={t.phone} required error={errors.phone}>
                      <input type="tel" value={formatPhoneInput(form.phone)} onChange={(e) => set('phone', e.target.value)} data-testid="ba-phone" className={inputCls} autoComplete="tel" />
                    </Field>
                    <Field label={t.state} required error={errors.state}>
                      <select value={form.state} onChange={(e) => set('state', e.target.value)} data-testid="ba-state" className={inputCls}>
                        <option value="">{t.select}</option>
                        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                  </div>
                </StepShell>
              )}

              {/* Step 1 — Licensing */}
              {step === 1 && (
                <StepShell title={t.s2Title} subtitle={t.s2Sub}>
                  <div>
                    <Label>{t.licensedQ} <span className="text-rose-600">*</span></Label>
                    <div className="mt-2 flex gap-2" role="radiogroup" aria-required="true">
                      <YesNo value={form.is_licensed} onChange={(v) => set('is_licensed', v)} name="is_licensed" testid="ba-licensed" yes={t.yes} no={t.no} />
                    </div>
                    {errors.is_licensed && <p className="text-xs text-rose-600 mt-1">{errors.is_licensed}</p>}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={t.npn}>
                      <input value={form.npn} onChange={(e) => set('npn', e.target.value)} data-testid="ba-npn" className={inputCls} inputMode="numeric" />
                    </Field>
                    <Field label={t.agency}>
                      <input value={form.current_agency} onChange={(e) => set('current_agency', e.target.value)} data-testid="ba-current-agency" className={inputCls} />
                    </Field>
                    <Field label={t.years}>
                      <input type="number" min="0" max="80" value={form.years_experience} onChange={(e) => set('years_experience', e.target.value)} data-testid="ba-years" className={inputCls} />
                    </Field>
                  </div>
                </StepShell>
              )}

              {/* Step 2 — Interests */}
              {step === 2 && (
                <StepShell title={t.s3Title} subtitle={t.s3Sub}>
                  <div className="grid sm:grid-cols-2 gap-2.5">
                    {(meta.areas_of_interest || []).map((a) => {
                      const selected = form.areas_of_interest.includes(a);
                      return (
                        <button
                          type="button"
                          key={a}
                          onClick={() => toggleArea(a)}
                          data-testid={`ba-area-${a.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                          className={`flex items-center gap-2 p-3 rounded-xl border text-left text-sm transition
                            ${selected ? 'border-[#0f2a5e] bg-[#0f2a5e]/[0.04] text-[#0f2a5e] font-medium' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                        >
                          <span className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${selected ? 'bg-[#0f2a5e] text-white' : 'border border-slate-300'}`}>
                            {selected && <Check size={11} />}
                          </span>
                          {a}
                        </button>
                      );
                    })}
                    {(meta.areas_of_interest || []).length === 0 && (
                      <p className="text-sm text-slate-400 sm:col-span-2">{t.s3Skip}</p>
                    )}
                  </div>
                </StepShell>
              )}

              {/* Step 3 — Review + consent */}
              {step === 3 && (
                <StepShell title={t.s4Title} subtitle={t.s4Sub}>
                  <dl className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 text-sm" data-testid="ba-review">
                    <ReviewRow label={t.reviewName} value={`${form.first_name} ${form.last_name}`.trim() || '—'} />
                    <ReviewRow label={t.reviewEmail} value={form.email || '—'} />
                    <ReviewRow label={t.reviewPhone} value={form.phone || '—'} />
                    <ReviewRow label={t.reviewState} value={form.state || '—'} />
                    <ReviewRow label={t.reviewLicensed} value={form.is_licensed === null ? '—' : form.is_licensed ? t.yes : t.no} />
                    {form.areas_of_interest.length > 0 && <ReviewRow label={t.reviewInterests} value={form.areas_of_interest.join(', ')} />}
                  </dl>

                  <Field label={t.comments}>
                    <textarea rows={3} value={form.comments} onChange={(e) => set('comments', e.target.value)} data-testid="ba-comments" className={inputCls} maxLength={2000} />
                  </Field>

                  <label className="flex items-start gap-3 text-sm text-slate-700">
                    <input type="checkbox" checked={form.consent_accepted} onChange={(e) => set('consent_accepted', e.target.checked)} data-testid="ba-consent" className="mt-1" />
                    <span>{lang === 'en' ? (meta.consent_language || t.consentFallback) : t.consentFallback}</span>
                  </label>
                  {errors.consent_accepted && <p className="text-xs text-rose-600 -mt-1">{errors.consent_accepted}</p>}
                </StepShell>
              )}

              {/* Honeypot */}
              <div className="hidden" aria-hidden="true">
                <label>Do not fill this out</label>
                <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set('website', e.target.value)} />
              </div>

              {/* Nav */}
              <div className="mt-8 flex items-center justify-between gap-3">
                {step === 0 ? (
                  <Link to="/" className="text-sm text-slate-500 hover:text-[#0f2a5e]">{t.cancel}</Link>
                ) : (
                  <button type="button" onClick={back} data-testid="ba-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-[#0f2a5e]">
                    <ArrowLeft size={15} /> {t.back}
                  </button>
                )}

                {step < LAST_STEP ? (
                  <button key="ba-next" type="button" onClick={next} data-testid="ba-next" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0f2a5e] text-white font-semibold hover:bg-[#0a1f47] transition">
                    {t.continue} <ArrowRight size={16} />
                  </button>
                ) : (
                  <button key="ba-submit" type="button" onClick={submit} disabled={submitting} data-testid="ba-submit" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#b8862d] text-white font-semibold hover:bg-[#a67520] disabled:opacity-60 transition shadow-lg shadow-amber-900/10">
                    {submitting ? t.submitting : t.submit}
                  </button>
                )}
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}

// ── Primitives ───────────────────────────────────────────────
const inputCls = 'block w-full rounded-lg border border-slate-300 shadow-sm px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0f2a5e] focus:border-[#0f2a5e] transition';
const Label = ({ children }) => <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">{children}</label>;
const Field = ({ label, required, error, children }) => (
  <div>
    <Label>{label}{required && <span className="text-rose-600"> *</span>}</Label>
    <div className="mt-1.5">{children}</div>
    {error && <p className="text-xs text-rose-600 mt-1">{error}</p>}
  </div>
);
const StepShell = ({ title, subtitle, children }) => (
  <div className="animate-[fadeIn_0.3s_ease]">
    <h2 className="text-xl font-bold text-slate-900">{title}</h2>
    {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    <div className="mt-6 space-y-5">{children}</div>
  </div>
);
const ReviewRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 px-4 py-2.5">
    <dt className="text-slate-500">{label}</dt>
    <dd className="font-medium text-slate-800 text-right">{value}</dd>
  </div>
);
const YesNo = ({ value, onChange, name, testid, yes, no }) => (
  <>
    {[{ v: true, label: yes, k: 'yes' }, { v: false, label: no, k: 'no' }].map((o) => (
      <label key={o.k} className={`px-5 py-2.5 rounded-lg border cursor-pointer text-sm font-medium transition ${
        value === o.v ? 'bg-[#0f2a5e] text-white border-[#0f2a5e]' : 'bg-white border-slate-300 text-slate-700 hover:border-[#0f2a5e]'
      }`}>
        <input type="radio" name={name} className="sr-only" checked={value === o.v} onChange={() => onChange(o.v)} data-testid={`${testid}-${o.k}`} />
        {o.label}
      </label>
    ))}
  </>
);
